import { store } from '../store';
import { featureFlags } from '../config/featureFlags';
import { getClientAppGateHeaders } from '../utils/clientAppGate';

const API_BASE_URL = featureFlags.general.apiUrl;

interface ApiResponse<T = any> {
  data: T;
  success: boolean;
  message?: string;
  error?: string;
}

class ApiService {
  private getAuthHeaders(endpoint: string, options: RequestInit = {}): Record<string, string> {
    const state = store.getState().auth;
    const headers: Record<string, string> = {};
    
    // Always set Content-Type for login endpoints or if not FormData
    if (endpoint.includes('/auth/login') || !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Determine context based on endpoint (support both /admin and /api/admin prefixes)
    if (endpoint.includes('/admin/') || endpoint.includes('/notifications/')) {
      headers['X-Context'] = 'admin';
    } else {
      headers['X-Context'] = 'retail';
    }

    // Don't add X-Type header for login endpoints to let backend determine auth type
    if (!endpoint.includes('/auth/login')) {
      headers['X-Type'] = state.type === 'SESSION' ? 'web' : 'api';
    }

    // Add Authorization header only for JWT auth and non-login endpoints
    if (state.token && state.type === 'JWT' && !endpoint.includes('/auth/login')) {
      headers['Authorization'] = `Bearer ${state.token}`;
    }

    Object.assign(headers, getClientAppGateHeaders());

    return headers;
  }

  async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const state = store.getState().auth;
      const baseHeaders = this.getAuthHeaders(endpoint, options);
      
      // Merge with custom headers, allowing overrides
      const headers = {
        ...baseHeaders,
        ...(options.headers || {}),
      };

      const requestOptions: RequestInit = {
        ...options,
        headers,
        // Always include credentials for login and session auth
        credentials: endpoint.includes('/auth/login') || state.type === 'SESSION' ? 'include' : 'omit',
      };

      const response = await fetch(`${API_BASE_URL}${endpoint}`, requestOptions);

      if (!response.ok) {
        // Improve diagnostics: capture raw body, try JSON parse if applicable
        const raw = await response.text();
        let parsed: any = null;
        try { parsed = raw ? JSON.parse(raw) : null; } catch {}
        const serverMessage = parsed?.message || parsed?.error || parsed?.errors?.[0];
        const msg = serverMessage || `HTTP error! status: ${response.status}`;
        console.error('API request failed:', {
          endpoint: `${API_BASE_URL}${endpoint}`,
          status: response.status,
          statusText: response.statusText,
          body: raw
        });
        throw new Error(msg);
      }

      const result = await response.json();
      // Support both wrapped { success, data, ...paginationFields } and bare objects.
      // Preserve pagination metadata (totalPages, page, totalElements, generatedAt, etc.)
      // by spreading the full result minus the reserved keys we override.
      const hasDataKey = result != null && Object.prototype.hasOwnProperty.call(result, 'data');
      const actualData = hasDataKey ? result.data : result;
      const meta: Record<string, unknown> = {};
      if (hasDataKey && typeof result === 'object') {
        for (const key of Object.keys(result)) {
          if (key !== 'data' && key !== 'success') {
            meta[key] = result[key];
          }
        }
      }
      return {
        ...meta,
        data: actualData,
        success: true,
      };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        data: null as T,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  // GET request helper
  async get<T = any>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  // POST request helper
  async post<T = any>(
    endpoint: string,
    data?: any,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    // For login endpoints, always stringify the data
    const body = endpoint.includes('/auth/login') 
      ? JSON.stringify(data)
      : (data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined));

    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body,
    });
  }

  // PUT request helper
  async put<T = any>(
    endpoint: string,
    data?: any,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request helper
  async delete<T = any>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // Asset Token Investment methods
  async getActiveAssetTokens(): Promise<ApiResponse<any[]>> {
    return this.get('/retail/asset-tokens/active');
  }

  async getAssetTokenById(tokenId: number): Promise<ApiResponse<any>> {
    return this.get(`/retail/asset-tokens/${tokenId}`);
  }

  async getAssetTokenBySymbol(tokenSymbol: string): Promise<ApiResponse<any>> {
    return this.get(`/retail/asset-tokens/symbol/${tokenSymbol}`);
  }

  async investInAssetToken(investmentData: {
    assetTokenId: number;
    investmentAmount: number;
    transactionHash?: string;
  }): Promise<ApiResponse<any>> {
    return this.post('/retail/asset-tokens/invest', investmentData);
  }

  async getMyAssetTokenInvestments(): Promise<ApiResponse<any[]>> {
    return this.get('/retail/asset-tokens/my-investments');
  }

  // ── Stablecoin: fetch enabled configs from admin ────────────────────────
  async getEnabledStablecoinConfigs(): Promise<ApiResponse<StablecoinConfigResponse[]>> {
    return this.get<StablecoinConfigResponse[]>('/stablecoin/configs');
  }

  // ── Retail strategy analytics (DB-backed — original / rollback path) ─────
  async getRetailStrategyTrades(strategyId: number, limit: number = 10): Promise<ApiResponse<any[]>> {
    return this.get(`/api/retail/trading/strategies/${strategyId}/trades?limit=${limit}`);
  }

  async getRetailStrategyPositions(strategyId: number): Promise<ApiResponse<any[]>> {
    return this.get(`/api/retail/trading/strategies/${strategyId}/positions`);
  }

  async getRetailStrategyPnlHistory(strategyId: number, days: number = 30): Promise<ApiResponse<any[]>> {
    return this.get(`/api/retail/trading/strategies/${strategyId}/pnl-history?days=${days}`);
  }

  // ── KuCoin-sourced analytics (more accurate live data) ───────────────────
  // The DB-backed methods above remain intact and serve as the rollback path.
  async getRetailStrategyKucoinAnalytics(strategyId: number, limit: number = 200): Promise<ApiResponse<{
    summary: {
      strategyId: number;
      strategyName: string;
      closedPositionCount: number;
      totalPnl: number;
      grossPnl?: number;
      totalTradeFee: number;
      totalFundingFee: number;
      netPnlAfterFees: number;
      winRate: number;
      winCount: number;
      lossCount: number;
      dataSource: string;
    };
    closedTrades: Array<{
      symbol: string;
      side: string;
      size: string;
      openPrice: string;
      closePrice: string;
      pnl: string;
      tradeFee: string;
      fundingFee: string;
      leverage: number;
      settleCurrency: string;
      openTime: string | null;
      closeTime: string | null;
      openTimeMs: number | null;
      closeTimeMs: number | null;
    }>;
    pnlHistory: Array<{
      date: string;
      pnl: number;
      cumulativePnl: number;
    }>;
  }>> {
    return this.get(`/api/retail/trading/strategies/${strategyId}/kucoin-analytics?limit=${limit}`);
  }
}

export const apiService = new ApiService();

// Stablecoin Config type (matches backend StablecoinConfigDto)
export interface StablecoinConfigResponse {
  id: number;
  symbol: string;
  name: string;
  network: string;
  enabled: boolean;
  transactionLimit: number | null;
  dailyLimit: number | null;
  minimumAmount: number;
  description: string | null;
  contractAddress: string | null;
  icon: string | null;
  convertEnabled: boolean;
  sendEnabled: boolean;
  receiveEnabled: boolean;
  createdAt: string;
  updatedAt: string;
} 